import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminPressKitService } from '../services/admin.pressKit.service'
import type { PressKitStatWithId, PressKitStatFormValues } from '@/features/press-kit/types'

interface UseAdminPressKitStatsResult {
  stats: PressKitStatWithId[]
  loading: boolean
  error: string | null
  createStat: (values: PressKitStatFormValues) => Promise<void>
  updateStat: (id: string, values: Partial<PressKitStatFormValues>) => Promise<void>
  updateStatOrder: (id: string, order: number) => Promise<void>
  deleteStat: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminPressKitStats(): UseAdminPressKitStatsResult {
  const [stats, setStats] = useState<PressKitStatWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminPressKitService
      .getAllStats()
      .then(setStats)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load stats'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createStat = useCallback(async (values: PressKitStatFormValues): Promise<void> => {
    await adminPressKitService.createStat(values, stats.length)
    refresh()
  }, [stats.length, refresh])

  const updateStat = useCallback(async (id: string, values: Partial<PressKitStatFormValues>): Promise<void> => {
    await adminPressKitService.updateStat(id, values)
    refresh()
  }, [refresh])

  const updateStatOrder = useCallback(async (id: string, order: number): Promise<void> => {
    await adminPressKitService.updateStatOrder(id, order)
    refresh()
  }, [refresh])

  const deleteStat = useCallback(async (id: string): Promise<void> => {
    await adminPressKitService.deleteStat(id)
    refresh()
  }, [refresh])

  return { stats, loading, error, createStat, updateStat, updateStatOrder, deleteStat, refresh }
}
